import { useEffect, useState } from "react";
import { ReceiptText, MessageCircle, Smartphone, Download, Copy, Check, Ban, Loader2 } from "lucide-react";
import { api, money, errorMessage } from "../api";
import { PaperSelect, printBill } from "../lib/printdoc";
import { useAuth } from "../auth/AuthContext";
import { fmtDT } from "../lib/units";
import { DateInput } from "../components/DateInput";

function ChargeToRoom({ bill, branch, onDone }) {
  const [guests, setGuests] = useState(null);
  const [pick, setPick] = useState("");
  const [err, setErr] = useState("");
  if (bill.room_charge) return <div className="auth-alert" data-testid="room-charge-banner">Charged to Room {bill.room_charge.room_number} ({bill.room_charge.booking_ref} · {bill.room_charge.guest}){bill.room_charge.settled_at ? " · settled at checkout" : " · to be collected at checkout"}</div>;
  if (bill.status === "cancelled") return null;
  const open = () => api.get(`/hotel/in-house?branch=${branch}`).then(({ data }) => setGuests(data)).catch((e) => setErr(errorMessage(e)));
  const go = () => api.post(`/sales/${bill.id}/charge-to-room`, { booking_id: pick }).then(({ data }) => { setGuests(null); onDone(data); }).catch((e) => setErr(errorMessage(e)));
  return (
    <div data-testid="charge-to-room">
      {guests === null ? <button className="secondary-button" onClick={open} data-testid="charge-to-room-button">Charge to room</button> : (
        <div style={{ display: "flex", gap: 6 }}>
          <select value={pick} onChange={(e) => setPick(e.target.value)} data-testid="charge-to-room-select"><option value="">{guests.length ? "— In-house guest —" : "No in-house guests"}</option>{guests.map((g) => <option key={g.id} value={g.id}>Room {g.room_number} · {g.guest?.name} · {g.ref}</option>)}</select>
          <button className="primary-button" disabled={!pick} onClick={go} data-testid="charge-to-room-confirm">Charge</button>
        </div>)}
      {err && <div className="auth-alert error" data-testid="charge-to-room-error">{err}</div>}
    </div>
  );
}

function CancelInvoice({ bill, onDone }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { const { data } = await api.post(`/sales/${bill.id}/cancel`, { reason }); setOpen(false); setReason(""); onDone(data); }
    catch (e2) { setErr(errorMessage(e2)); } finally { setBusy(false); }
  };
  if (!open) return <button className="secondary-button" onClick={() => setOpen(true)} data-testid="cancel-invoice-button"><Ban size={14}/> Cancel invoice</button>;
  return (
    <form onSubmit={submit} className="panel" style={{ padding: 12 }} data-testid="cancel-invoice-form">
      <p className="form-hint">The invoice number {bill.invoice_no} is kept (not reused). Stock and loyalty points are reversed.</p>
      <label>Reason (required)<input value={reason} required onChange={(e) => setReason(e.target.value)} placeholder="e.g. Wrong table billed" data-testid="cancel-invoice-reason"/></label>
      {err && <div className="auth-alert error" data-testid="cancel-invoice-error">{err}</div>}
      <div style={{ display: "flex", gap: 8 }}>
        <button className="primary-button" disabled={busy} data-testid="cancel-invoice-confirm">{busy ? <Loader2 className="spin" size={14}/> : <Ban size={14}/>} Confirm cancel</button>
        <button type="button" className="secondary-button" onClick={() => setOpen(false)} data-testid="cancel-invoice-back">Back</button>
      </div>
    </form>
  );
}

const istDay = (offset) => new Date(Date.now() + 5.5 * 3600000 + offset * 86400000).toISOString().slice(0, 10);

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return fmtDT(iso);
  } catch { return iso; }
}

export function SendToGuest({ bill }) {
  const [phone, setPhone] = useState((bill.customer_phone || "").replace(/^\+?91/, ""));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  useEffect(() => { setPhone((bill.customer_phone || "").replace(/^\+?91/, "")); setMsg(null); }, [bill.id]); // eslint-disable-line
  const send = async () => {
    setBusy(true); setMsg(null);
    try { const { data } = await api.post(`/sales/${bill.id}/send-to-guest`, { phone }); setMsg(data.status === "accepted" ? { ok: true, t: `Sent on ${data.channel === "sms" ? "SMS" : "WhatsApp"} from your number` } : { t: `Not sent — ${data.error}` }); }
    catch (e) { setMsg({ t: errorMessage(e) }); } finally { setBusy(false); }
  };
  if (bill.status === "cancelled") return null;
  return (
    <div className="send-guest" data-testid="send-to-guest">
      <input inputMode="numeric" placeholder="Guest mobile" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="send-guest-phone" />
      <button className="primary-button" disabled={busy || !phone} onClick={send} data-testid="send-guest-button">{busy ? <Loader2 className="spin" size={14} /> : <MessageCircle size={14} />} Send bill from our number</button>
      {msg && <div className={`auth-alert ${msg.ok ? "info" : "error"}`} data-testid="send-guest-msg">{msg.t}</div>}
    </div>
  );
}

export default function BillView({ branch, outlet, lastBill, setLastBill }) {
  const [history, setHistory] = useState([]);
  const [selected, setSelected] = useState(lastBill || null);
  const [copied, setCopied] = useState(false);
  const { user } = useAuth() || {};
  const canCancel = ["owner", "admin", "manager"].includes(user?.role || "owner");

  const [flt, setFlt] = useState(() => ({ q: "", date_from: istDay(-30), date_to: istDay(0), status: "" }));
  const [searchErr, setSearchErr] = useState("");
  const load = async () => {
    try {
      const { data } = await api.get(`/sales`, { params: { branch, ...flt } });
      setHistory(data); setSearchErr("");
      if (!selected && data.length) setSelected(data[0]);
    } catch (e) { setSearchErr(errorMessage(e)); }
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [branch, lastBill, flt]);
  const setF = (k, v) => setFlt((x) => ({ ...x, [k]: v }));

  const bill = selected || lastBill;
  const o = bill?.outlet || outlet || {};

  const invoiceUrl = bill ? `${window.location.origin}/invoice/${bill.id}` : "";

  const buildText = () => {
    if (!bill) return "";
    const name = bill.customer_name ? ` ${bill.customer_name}` : "";
    const brand = o.display_name || "FynDine Restaurant";
    return [
      `Namaste${name}! 🙏`,
      ``,
      `Your tax invoice from *${brand}* is ready.`,
      `Invoice: ${bill.invoice_no}`,
      `Amount: ₹${bill.grand_total}`,
      ``,
      `Tap to view the full GST invoice:`,
      invoiceUrl,
      ``,
      `Thank you for dining with us!`,
    ].join("\n");
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(buildText()); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* noop */ }
  };

  const normalizePhone = (raw) => {
    if (!raw) return "";
    const digits = String(raw).replace(/\D/g, "");
    if (!digits) return "";
    // Already in international format with country code (12-15 digits)
    if (digits.length >= 11 && digits.length <= 15) {
      // If starts with 0 and length 11, likely trunk prefix -> treat remaining 10 as Indian local
      if (digits.length === 11 && digits.startsWith("0")) return "91" + digits.slice(1);
      return digits;
    }
    // Plain 10-digit Indian mobile -> prepend India country code 91
    if (digits.length === 10) return "91" + digits;
    // Leading 0 + 10-digit Indian mobile
    if (digits.length === 11 && digits.startsWith("0")) return "91" + digits.slice(1);
    return digits;
  };

  const phone = normalizePhone(bill?.customer_phone);
  const smsPhone = bill?.customer_phone ? (phone.startsWith("91") ? "+" + phone : phone) : "";
  const text = encodeURIComponent(buildText());

  return (
    <section className="bill-layout" data-testid="bill-view">
      <div className="bill-form panel">
        <div className="panel-head">
          <div><span className="overline">CUSTOMER COMMUNICATION</span><h2>GST-compliant bill</h2></div>
          <ReceiptText size={21} color="#e67e32" />
        </div>

        <div className="bill-search" data-testid="bill-search">
          <label>Search<input value={flt.q} onChange={(e) => setF("q", e.target.value)} placeholder="Mobile (partial), name or invoice no." data-testid="bill-search-input" /></label>
          <div className="form-grid">
            <label>From<DateInput value={flt.date_from} max={flt.date_to} onChange={(e) => setF("date_from", e.target.value)} data-testid="bill-date-from" /></label>
            <label>To<DateInput value={flt.date_to} min={flt.date_from} max={istDay(0)} onChange={(e) => setF("date_to", e.target.value)} data-testid="bill-date-to" /></label>
            <label>Status<select value={flt.status} onChange={(e) => setF("status", e.target.value)} data-testid="bill-status-filter"><option value="">All</option><option value="active">Active</option><option value="cancelled">Cancelled</option></select></label>
          </div>
          <small className="muted-label" data-testid="bill-search-count">{history.length} invoices · max range 1 year</small>
          {searchErr && <div className="auth-alert error" data-testid="bill-search-error">{searchErr}</div>}
        </div>
        {history.length > 0 && (
          <label>Select invoice
            <select value={history.some((h) => h.id === bill?.id) ? bill.id : ""} onChange={(e) => setSelected(history.find((h) => h.id === e.target.value))} data-testid="bill-select">
              {!history.some((h) => h.id === bill?.id) && <option value="">— Pick an invoice —</option>}
              {history.map((h) => (
                <option key={h.id} value={h.id}>{`${h.invoice_no} · ${money(h.grand_total)} · ${fmtDate(h.created_at)}${h.customer_name ? ` · ${h.customer_name}` : ""}${h.customer_phone ? ` · ${h.customer_phone}` : ""}${h.status === "cancelled" ? " · CANCELLED" : ""}`}</option>
              ))}
            </select>
          </label>
        )}

        {!bill && <div className="empty">No invoices yet. Create one from POS &amp; Sales.</div>}

        {bill && (
          <>
            <label>Customer mobile<input placeholder="Enter 10-digit mobile number" value={bill.customer_phone || ""} readOnly data-testid="bill-phone"/></label>
            <label>Shareable invoice link<input value={invoiceUrl} readOnly data-testid="bill-link"/></label>
            <p className="form-hint">The link opens a hosted GST-compliant invoice page the customer can view on any device and save as PDF. WhatsApp &amp; SMS send a short message with this link.</p>
            <SendToGuest bill={bill} />
            <div className="share-actions">
              <a className={`share-button whatsapp ${!phone ? "disabled" : ""}`} href={phone ? `https://wa.me/${phone}?text=${text}` : "#"} target="_blank" rel="noreferrer" data-testid="share-bill-whatsapp"><MessageCircle size={15}/> Send on WhatsApp</a>
              <a className={`share-button sms ${!phone ? "disabled" : ""}`} href={phone ? `sms:${smsPhone}?body=${text}` : "#"} data-testid="share-bill-sms"><Smartphone size={15}/> Send SMS link</a>
              <a className="share-button copy" href={invoiceUrl} target="_blank" rel="noreferrer" data-testid="open-bill-link"><Copy size={15}/> Open invoice</a>
              <button className="share-button copy" onClick={copy} data-testid="share-bill-copy">{copied ? <Check size={15}/> : <Copy size={15}/>} {copied ? "Copied" : "Copy message"}</button>
              <button className="share-button print" onClick={() => printBill(bill, () => window.print())} data-testid="share-bill-print"><Download size={15}/> Print / Save PDF</button>
              <PaperSelect testid="bill-paper-width" />
            </div>
            <ChargeToRoom bill={bill} branch={branch} onDone={(d) => { setSelected(d); load(); }} />
            {bill.status === "cancelled"
              ? <div className="auth-alert error" data-testid="invoice-cancelled-banner">Cancelled on {fmtDate(bill.cancelled_at)} by {bill.cancelled_by?.name || "owner"} — {bill.cancel_reason}</div>
              : canCancel && !bill.room_charge && <CancelInvoice bill={bill} onDone={(d) => { setSelected(d); load(); }}/>}
          </>
        )}
      </div>

      <div className="bill-preview panel">
        <span className="overline">INVOICE PREVIEW</span>
        {bill ? (
          <div className="receipt gst-receipt" id="print-area">
            <div className="receipt-brand">
              <b>{o.display_name || "FynDine Restaurant"}</b>
              {o.legal_name && <small>{o.legal_name}</small>}
              {(o.address || o.city) && <small>{o.address}{o.city ? ", " + o.city : ""}{o.pincode ? " · " + o.pincode : ""}</small>}
              {o.contact_phone && <small>Ph: {o.contact_phone}{o.contact_email ? " · " + o.contact_email : ""}</small>}
              <small>{o.gstin ? `GSTIN: ${o.gstin}` : ""}{o.gstin && o.fssai ? "  ·  " : ""}{o.fssai ? `FSSAI: ${o.fssai}` : ""}</small>
            </div>

            <div className="receipt-meta">
              <div><small>Invoice</small><b data-testid="bill-invoice-no">{bill.invoice_no}{bill.status === "cancelled" ? " (CANCELLED)" : ""}</b></div>
              <div><small>Date</small><b>{fmtDate(bill.created_at)}</b></div>
              <div><small>Channel</small><b>{bill.channel}</b></div>
              {bill.payment_mode && <div><small>Paid by</small><b data-testid="bill-payment-mode">{bill.payment_status === "pending" ? "UNPAID" : bill.payment_status === "partial" ? `PART PAID · ${money(bill.balance_due)} due` : bill.payments?.length > 1 ? bill.payments.map((p) => `${p.mode.toUpperCase()} ${money(p.amount)}`).join(" + ") : bill.payment_mode === "credit" ? "CREDIT (due)" : bill.payment_mode.toUpperCase()}</b></div>}
              {bill.table_label && <div><small>Table</small><b>{bill.table_label}</b></div>}
              {bill.waiter_name && <div><small>Served by</small><b>{bill.waiter_name}</b></div>}
              {bill.customer_name && <div><small>Customer</small><b>{bill.customer_name}</b></div>}
              {(bill.kot_numbers || []).length > 0 && <div style={{gridColumn:"1 / -1"}}><small>KOT reference</small><b>{bill.kot_numbers.join(", ")}</b></div>}
            </div>

            <table className="receipt-table">
              <thead><tr><th>Item</th><th>HSN</th><th>Qty</th><th>Rate</th><th>GST%</th><th>Total</th></tr></thead>
              <tbody>
                {bill.items.map((it, idx) => (
                  <tr key={idx}>
                    <td>{it.name}</td>
                    <td>{it.hsn_sac || "—"}</td>
                    <td>{it.qty}</td>
                    <td>₹{it.unit_price}</td>
                    <td>{it.gst_rate}%</td>
                    <td>₹{(it.line_amount + it.tax_amount).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="receipt-totals">
              <div><span>Subtotal</span><b>{money(bill.subtotal)}</b></div>
              {bill.interstate ? (
                <div><span>IGST</span><b>{money(bill.igst)}</b></div>
              ) : (
                <>
                  <div><span>CGST</span><b>{money(bill.cgst)}</b></div>
                  <div><span>SGST</span><b>{money(bill.sgst)}</b></div>
                </>
              )}
              {bill.service_charge > 0 && <div><span>Service charge</span><b>{money(bill.service_charge)}</b></div>}
              {bill.discount > 0 && <div><span>Discount</span><b>-{money(bill.discount)}</b></div>}
              <div className="grand-row"><span>Grand total</span><strong>{money(bill.grand_total)}</strong></div>
            </div>

            <div className="receipt-foot">
              <small>GST on reverse charge: No. Supply type: {bill.interstate ? "Interstate" : "Intrastate"}.</small>
              <small>Thank you for dining with us. All disputes subject to {o.city || "local"} jurisdiction.</small>
            </div>
          </div>
        ) : (
          <div className="empty">Create a sale from POS to see the GST bill here.</div>
        )}
      </div>
    </section>
  );
}
